import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandInput } from "@/components/brand/brand-input";
import { BrandTextarea } from "@/components/brand/brand-textarea";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	DashboardContent,
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { FounderProfileStoryEditor } from "@/components/FounderProfileStoryEditor";
import { CountrySelect } from "@/components/ui/country-select";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { ImageUpload } from "@/components/ui/image-upload";
import { toast } from "@/lib/toast";
import {
	displayNameSchema,
	taglineSchema,
	xHandleSchema,
} from "@/lib/validation";

type FounderProfile = {
	avatarUrl: string | null;
	country: string | null;
	name: string;
	slug: string | null;
	tagline: string | null;
	xHandle: string | null;
};

async function parseJson<T>(response: Response) {
	const payload = (await response.json().catch(() => null)) as T | null;
	if (!response.ok) {
		throw new Error(
			(payload &&
				typeof payload === "object" &&
				"error" in payload &&
				typeof payload.error === "string" &&
				payload.error) ||
				"Request failed",
		);
	}
	return payload as T;
}

export const Route = createFileRoute("/dashboard/founder")({
	component: DashboardFounderPage,
});

const founderSchema = z.object({
	avatarUrl: z.string().optional().or(z.literal("")),
	country: z.string().max(120).optional().or(z.literal("")),
	name: displayNameSchema,
	tagline: taglineSchema,
	xHandle: xHandleSchema,
});

type FounderForm = z.infer<typeof founderSchema>;

type Tab = "profile" | "story";

const TABS: { id: Tab; label: string; requiresFounder?: boolean }[] = [
	{ id: "profile", label: "Profile" },
	{ id: "story", label: "Founder Story", requiresFounder: true },
];

function DashboardFounderPage() {
	const queryClient = useQueryClient();
	const { user } = useDashboardContext();
	const [activeTab, setActiveTab] = useState<Tab>("profile");

	const founderQuery = useQuery({
		queryFn: async () =>
			parseJson<{ founder: FounderProfile | null }>(
				await fetch("/api/founders/me", {
					headers: { Accept: "application/json" },
				}),
			),
		queryKey: ["founder-me"],
	});

	const founder = founderQuery.data?.founder;

	const form = useForm<FounderForm>({
		resolver: zodResolver(founderSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: {
			avatarUrl: "",
			country: "",
			name: "",
			tagline: "",
			xHandle: "",
		},
	});

	useEffect(() => {
		if (founderQuery.isLoading) return;
		if (form.formState.isDirty) return;
		form.reset({
			avatarUrl: founder?.avatarUrl ?? "",
			country: founder?.country ?? "",
			name: founder?.name ?? user?.name ?? "",
			tagline: founder?.tagline ?? "",
			xHandle: founder?.xHandle ?? "",
		});
	}, [founder, founderQuery.isLoading, user?.name, form]);

	const saveMutation = useMutation({
		mutationFn: async (values: FounderForm) =>
			parseJson<{ founder: FounderProfile }>(
				await fetch("/api/founders/me", {
					method: "PATCH",
					headers: {
						Accept: "application/json",
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						avatarUrl: values.avatarUrl?.trim() || undefined,
						country: values.country?.trim() || undefined,
						name: values.name.trim(),
						tagline: values.tagline?.trim() || undefined,
						xHandle: values.xHandle?.trim() || undefined,
					}),
				}),
			),
		onSuccess: async (_data, values) => {
			await queryClient.invalidateQueries({ queryKey: ["founder-me"] });
			toast.success("Founder profile saved");
			form.reset(values);
		},
		onError: (error) => {
			toast.error("Failed to save", {
				description: error instanceof Error ? error.message : undefined,
			});
		},
	});

	const avatarUrl = form.watch("avatarUrl");
	const showAvatarFallback = !avatarUrl && !!user?.image;
	const loading = saveMutation.isPending;

	const hasFounder = !!founder?.slug;

	return (
		<DashboardLayout>
			<DashboardContent className="max-w-3xl">
				<div className="pb-5 mb-5">
					<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
						&gt; Dashboard / founder
					</p>
					<p className="mt-2 text-base text-muted-foreground">
						Your public profile linked from your agents.
					</p>
				</div>

				<div className="mb-5 flex border-b border-border">
					{TABS.map((tab) => {
						const disabled = tab.requiresFounder && !hasFounder;
						const isActive = activeTab === tab.id;
						return (
							<button
								key={tab.id}
								type="button"
								disabled={disabled}
								onClick={() => setActiveTab(tab.id)}
								className={`border-b-2 px-4 py-2 font-mono text-sm uppercase tracking-[0.14em] transition-colors -mb-px disabled:pointer-events-none disabled:opacity-40 ${
									isActive
										? "border-brand text-brand"
										: "border-transparent text-muted-foreground hover:text-brand"
								}`}
							>
								{tab.label}
							</button>
						);
					})}
				</div>

				{activeTab === "profile" && (
					<section className="border border-border bg-card">
						<div className="border-b border-border px-5 py-3">
							<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Profile
							</span>
						</div>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit((values) =>
									saveMutation.mutate(values),
								)}
								className="space-y-5 p-5"
								noValidate
							>
								<FormField
									control={form.control}
									name="avatarUrl"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Avatar</FormLabel>
											<ImageUpload
												value={field.value ?? ""}
												onChange={(value) => field.onChange(value)}
												pathPrefix="avatars"
												shape="circle"
												placeholder="Drop photo or click to browse"
											/>
											{showAvatarFallback && (
												<div className="flex items-center gap-2 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
													<img
														src={user.image!}
														alt="Sign-in photo"
														className="size-5 border border-border object-cover"
														referrerPolicy="no-referrer"
													/>
													<span>
														No avatar uploaded, sign-in photo used as fallback
													</span>
												</div>
											)}
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="name"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												Name <span className="text-brand">*</span>
											</FormLabel>
											<FormControl>
												<BrandInput
													autoFocus
													placeholder="Jane Doe"
													disabled={founderQuery.isLoading}
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="tagline"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Tagline</FormLabel>
											<FormControl>
												<BrandTextarea
													placeholder="One line about what you build."
													disabled={founderQuery.isLoading}
													maxLength={160}
													minHeight={64}
													maxHeight={120}
													{...field}
													value={field.value ?? ""}
												/>
											</FormControl>
											<FormDescription>
												Shown below your name on your public founder page.
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="xHandle"
									render={({ field }) => (
										<FormItem>
											<FormLabel>X handle</FormLabel>
											<FormControl>
												<BrandInput
													placeholder="@janedoe"
													disabled={founderQuery.isLoading}
													{...field}
													value={field.value ?? ""}
												/>
											</FormControl>
											<FormDescription>
												Shown on your public founder page.
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="country"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Country</FormLabel>
											<FormControl>
												<CountrySelect
													value={field.value ?? ""}
													onChange={(v) => field.onChange(v)}
												/>
											</FormControl>
											<FormDescription>
												Shown on your public founder page and your agents.
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<div className="flex items-center gap-3">
									<BrandButton type="submit" disabled={loading}>
										{loading ? (
											<Loader2 className="size-3.5 animate-spin" />
										) : (
											<Check className="size-3.5" />
										)}
										{loading ? "Saving" : "Save profile"}
									</BrandButton>
									{founder?.slug ? (
										<Link
											to="/founders/$founderSlug"
											params={{ founderSlug: founder.slug }}
											className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
										>
											View public page →
										</Link>
									) : null}
								</div>
							</form>
						</Form>
					</section>
				)}

				{activeTab === "story" && hasFounder && (
					<section className="border border-border bg-card">
						<div className="border-b border-border px-5 py-3">
							<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Founder story
							</span>
						</div>
						<div className="space-y-4 p-5">
							<p className="text-base text-muted-foreground">
								A short structured note on who you are, how you work, and what
								you're building next. Shown on your public founder page.
							</p>
							<FounderProfileStoryEditor />
						</div>
					</section>
				)}
			</DashboardContent>
		</DashboardLayout>
	);
}
